/**
 * services/interactives.js — interactive session message types + send helper.
 *
 * The agent picker (#94) shows a small grid of "interactive" types the agent
 * can send to a customer. Each entry in the registry below describes:
 *
 *   - `id`             — stable internal key, also used in the form's React state
 *   - `label`          — short tile name on the picker grid
 *   - `hint`           — one-line subtitle on the tile
 *   - `icon`           — single emoji glyph (matches the existing attach menu style)
 *   - `defaultState`   — initial form state (so each form can KISS reach for it)
 *   - `validate(state)` — returns null if valid, otherwise a string error
 *   - `buildPayload(state, phone)` — returns the Meta Cloud API payload
 *   - `optimisticBody(state)` — the body shape for the in-thread bubble preview
 *
 * Adding a new type later = one new entry. The picker, form host, and bubble
 * renderer all read from this single registry, so no plumbing has to change.
 *
 * MVP types (#94 v1):
 *   - reply_buttons   : 1–3 quick-reply buttons (text only)
 *   - cta_url         : single URL button
 *   - location_request: ask the customer to share their location
 *
 * Deferred (provisioned for, not built):
 *   - List Message — explicitly out of scope (mobile UX is poor)
 *   - Address Request — India-only, niche
 *   - Catalog / Product / Multi-Product — needs Meta catalog wiring
 *   - Flow Message — full form builder, separate phase
 *   - Star rating — not a native WA type, would need a Flow
 */

import * as api from './api.js';
import {
  appendMessage,
  patchMessage,
} from '../stores/messages.js';
import { MESSAGE_STATUS, DIRECTION } from '../shared/constants.js';
import { hapticTap } from './haptics.js';
import { resolveActiveChannel as _getActiveChannelId } from './channel-resolve.js';

// _getActiveChannelId is the shared resolver in services/channel-resolve.js
// (#120). Previously this file had a forked copy that skipped the
// "last inbound's to_phone" priority — meaning interactive replies could
// route to the wrong channel on multi-channel accounts. Fixed by sharing.

// ── Validators (kept tiny — Meta does the real validation) ────────────────
function _trimmed(s) { return String(s || '').trim(); }
function _validateBody(state) {
  if (!_trimmed(state.body)) return 'Body text is required';
  if (state.body.length > 1024) return 'Body must be 1024 characters or fewer';
  return null;
}
function _validateUrl(s) {
  if (!_trimmed(s)) return 'URL is required';
  if (!/^https?:\/\/\S+$/i.test(s.trim())) return 'URL must start with http:// or https://';
  return null;
}

// ── Registry ──────────────────────────────────────────────────────────────
//
// Each entry is a value object — components import this map directly and
// loop over its values (Object.values) to render the picker grid.
//
export const INTERACTIVE_TYPES = {

  // ── Reply Buttons (1–3 quick replies) ───────────────────────────────────
  reply_buttons: {
    id:    'reply_buttons',
    label: 'Reply buttons',
    hint:  '1–3 tap buttons',
    icon:  '💬',
    defaultState: {
      body:    '',
      header:  '',
      footer:  '',
      // Two prefilled buttons — Yes/No is the most common binary case
      // and the user explicitly asked for "2 prefilled" as the default.
      buttons: [{ title: 'Yes' }, { title: 'No' }],
    },
    validate(state) {
      const bodyErr = _validateBody(state);
      if (bodyErr) return bodyErr;
      if (state.header && state.header.length > 60) return 'Header must be 60 characters or fewer';
      if (state.footer && state.footer.length > 60) return 'Footer must be 60 characters or fewer';
      const filled = (state.buttons || []).filter(b => _trimmed(b.title));
      if (filled.length < 1) return 'Add at least one button';
      if (filled.length > 3) return 'Maximum 3 buttons';
      for (const b of filled) {
        if (b.title.length > 20) return 'Button labels must be 20 characters or fewer';
      }
      return null;
    },
    buildPayload(state, phone) {
      const filled = state.buttons.filter(b => _trimmed(b.title));
      const interactive = {
        type: 'button',
        body: { text: state.body },
        action: {
          buttons: filled.map((b, i) => ({
            type: 'reply',
            reply: { id: `btn_${i}`, title: b.title.trim() },
          })),
        },
      };
      if (_trimmed(state.header)) {
        interactive.header = { type: 'text', text: state.header.trim() };
      }
      if (_trimmed(state.footer)) {
        interactive.footer = { text: state.footer.trim() };
      }
      return {
        messaging_product: 'whatsapp',
        recipient_type:    'individual',
        to:                phone,
        type:              'interactive',
        interactive,
        _phone_number_id:  _getActiveChannelId(phone),
      };
    },
    // For the agent's own bubble — preview the body + a flat button list
    // so they see what they sent without reverse-engineering the payload.
    optimisticBody(state) {
      return {
        interactive: {
          type:    'button',
          header:  state.header ? { type: 'text', text: state.header } : undefined,
          body:    { text: state.body },
          footer:  state.footer ? { text: state.footer } : undefined,
          action:  { buttons: state.buttons.filter(b => _trimmed(b.title)).map((b, i) => ({
            type: 'reply', reply: { id: `btn_${i}`, title: b.title.trim() },
          })) },
        },
      };
    },
  },

  // ── CTA URL (single tap-to-open button) ─────────────────────────────────
  cta_url: {
    id:    'cta_url',
    label: 'Visit link',
    hint:  'Open URL button',
    icon:  '🔗',
    defaultState: {
      body:    '',
      header:  '',
      footer:  '',
      label:   'Visit website',
      url:     'https://',
    },
    validate(state) {
      const bodyErr = _validateBody(state);
      if (bodyErr) return bodyErr;
      if (state.header && state.header.length > 60) return 'Header must be 60 characters or fewer';
      if (state.footer && state.footer.length > 60) return 'Footer must be 60 characters or fewer';
      if (!_trimmed(state.label)) return 'Button label is required';
      if (state.label.length > 20) return 'Button label must be 20 characters or fewer';
      const urlErr = _validateUrl(state.url);
      if (urlErr) return urlErr;
      return null;
    },
    buildPayload(state, phone) {
      const interactive = {
        type: 'cta_url',
        body: { text: state.body },
        action: {
          name: 'cta_url',
          parameters: {
            display_text: state.label.trim(),
            url:          state.url.trim(),
          },
        },
      };
      if (_trimmed(state.header)) {
        interactive.header = { type: 'text', text: state.header.trim() };
      }
      if (_trimmed(state.footer)) {
        interactive.footer = { text: state.footer.trim() };
      }
      return {
        messaging_product: 'whatsapp',
        recipient_type:    'individual',
        to:                phone,
        type:              'interactive',
        interactive,
        _phone_number_id:  _getActiveChannelId(phone),
      };
    },
    optimisticBody(state) {
      return {
        interactive: {
          type:    'cta_url',
          header:  state.header ? { type: 'text', text: state.header } : undefined,
          body:    { text: state.body },
          footer:  state.footer ? { text: state.footer } : undefined,
          action:  {
            name: 'cta_url',
            parameters: { display_text: state.label, url: state.url },
          },
        },
      };
    },
  },

  // ── Location Request ────────────────────────────────────────────────────
  location_request: {
    id:    'location_request',
    label: 'Request location',
    hint:  'Ask for GPS share',
    icon:  '📍',
    defaultState: {
      body: 'Kindly share your location.',
    },
    validate(state) {
      return _validateBody(state);
    },
    buildPayload(state, phone) {
      return {
        messaging_product: 'whatsapp',
        recipient_type:    'individual',
        to:                phone,
        type:              'interactive',
        interactive: {
          type: 'location_request_message',
          body: { text: state.body },
          action: { name: 'send_location' },
        },
        _phone_number_id: _getActiveChannelId(phone),
      };
    },
    optimisticBody(state) {
      return {
        interactive: {
          type: 'location_request_message',
          body: { text: state.body },
          action: { name: 'send_location' },
        },
      };
    },
  },
};

// ── Send helper ───────────────────────────────────────────────────────────

/**
 * Send an interactive message of the given type. Drives the same
 * optimistic → API → reconcile flow as sendText/sendMedia so the agent
 * sees an immediate bubble that gets stamped with the real wamid once
 * the platform replies.
 */
export async function sendInteractive(typeId, phone, state) {
  const def = INTERACTIVE_TYPES[typeId];
  if (!def) throw new Error(`Unknown interactive type: ${typeId}`);

  const validationErr = def.validate(state);
  if (validationErr) throw new Error(validationErr);

  // Phase B (#136) — same client_id pattern as sendText etc.
  const clientId = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : 'cid_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);

  // S5 — composite-key thread store. Resolve the channel ONCE here so
  // every store mutation below lands on the same (phone, channel)
  // bucket as the payload's _phone_number_id.
  const channelPhoneId = _getActiveChannelId(phone);

  const optimistic = {
    id:        clientId,
    clientId,
    wamid:     null,
    type:      'interactive',
    direction: DIRECTION.OUTBOUND,
    fromMe:    true,
    status:    MESSAGE_STATUS.PENDING,
    timestamp: Math.floor(Date.now() / 1000),
    ...def.optimisticBody(state),
  };
  appendMessage(phone, channelPhoneId, optimistic);
  hapticTap();

  try {
    const payload = def.buildPayload(state, phone);
    payload.client_id = clientId;
    const result = await api.sendMessage(payload);
    const realWamid =
      result?.messages?.[0]?.id
      || result?.data?.messages?.[0]?.id
      || result?.message?.queue_id
      || null;
    patchMessage(phone, channelPhoneId, optimistic.id, {
      status: MESSAGE_STATUS.SENT,
      wamid:  realWamid,
      id:     realWamid || optimistic.id,
    });
    return { ok: true, wamid: realWamid };
  } catch (e) {
    patchMessage(phone, channelPhoneId, optimistic.id, { status: MESSAGE_STATUS.FAILED, error: e.message });
    throw e;
  }
}
